// Static option vocabularies for the carousel dynamic-query builder.
// Enum values mirror ACE's domain types (LifecycleState, CompetitionType,
// Gender) and the cookbook filter vocabulary. Catalog-backed filters
// (players, competitions, seasons) are resolved via typeaheads, not listed
// here. Sources/filters are limited to what ACE's DynamicQueryRegistry can
// actually resolve — only "matches" and "channels" have handlers today.

export interface Opt { value: string; label: string }

export type SourceCode = 'matches' | 'channels'

export const SOURCE_OPTIONS: Array<Opt & { value: SourceCode }> = [
  { value: 'matches', label: 'Matches' },
  { value: 'channels', label: 'Channels' },
]

// --- match filter enums ---
// Shared tennis vocabularies live in constants/categories.ts (the source of
// truth reused across match/tournament UIs); re-exported here so the carousel
// builder has a single vocabulary import surface.
export { LIFECYCLE_OPTIONS, SURFACE_OPTIONS, ROUND_OPTIONS } from './categories'

export const CATEGORY_OPTIONS: Opt[] = [
  { value: 'ATP', label: 'ATP' },
  { value: 'WTA', label: 'WTA' },
  { value: 'ITF', label: 'ITF' },
]

export const FORMAT_OPTIONS: Opt[] = [
  { value: 'SINGLES', label: 'Singles' },
  { value: 'DOUBLES', label: 'Doubles' },
  { value: 'MIXED', label: 'Mixed' },
]

export const GENDER_OPTIONS: Opt[] = [
  { value: 'MEN', label: 'Men' },
  { value: 'WOMEN', label: 'Women' },
  { value: 'MIXED', label: 'Mixed' },
]

export const PLAYER_MATCH_OPTIONS: Opt[] = [
  { value: 'OR', label: 'Any (OR) — each player’s matches' },
  { value: 'AND', label: 'All (AND) — head-to-head only' },
]

// --- card fields (which metadata renders on each card) ---
export const CARD_FIELD_OPTIONS: Opt[] = [
  { value: 'title', label: 'Title' },
  { value: 'subtitle', label: 'Subtitle' },
  { value: 'round', label: 'Round' },
  { value: 'start_time', label: 'Start time' },
  { value: 'duration', label: 'Duration' },
  { value: 'image', label: 'Image' },
  { value: 'badge', label: 'Badge' },
]

// --- sort options, keyed by the source(s) they apply to ---
const SORT_BY_SOURCE: Record<SourceCode, Opt[]> = {
  matches: [
    { value: 'live_first', label: 'Live first' },
    { value: 'start_time_asc', label: 'Start time ↑' },
    { value: 'start_time_desc', label: 'Start time ↓' },
  ],
  channels: [{ value: 'channel_order', label: 'Channel order' }],
}

// The sort choices valid for the current source selection: the union of each
// source's sorts.
export const sortOptionsForSources = (sources: SourceCode[]): Opt[] => {
  const seen = new Set<string>()
  const out: Opt[] = []
  for (const s of sources) {
    for (const opt of SORT_BY_SOURCE[s] ?? []) {
      if (!seen.has(opt.value)) { seen.add(opt.value); out.push(opt) }
    }
  }
  return out
}
